import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Dumbbell,
  Plus,
  Edit2,
  Trash2,
  Search,
  Copy,
  Eye,
  Calendar,
  Clock,
  Target,
  Users,
  Star,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  Sparkles,
  Flame,
  Trophy,
  Heart,
  Activity,
  Medal,
  Filter,
  Globe,
  Lock,
  Play,
  Wand2,
  Loader2
} from 'lucide-react';

const CATEGORY_CONFIG = {
  muscle_building: { name: 'Muscle Building', icon: Dumbbell, color: 'text-red-400', bg: 'bg-red-500/10' },
  fat_loss: { name: 'Fat Loss', icon: Flame, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  strength: { name: 'Strength', icon: Trophy, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  endurance: { name: 'Endurance', icon: Heart, color: 'text-green-400', bg: 'bg-green-500/10' },
  flexibility: { name: 'Flexibility', icon: Activity, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  sport_specific: { name: 'Sport Specific', icon: Medal, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  general_fitness: { name: 'General Fitness', icon: Target, color: 'text-teal-400', bg: 'bg-teal-500/10' }
};

const DIFFICULTY_CONFIG = {
  beginner: { label: 'Beginner', color: 'text-green-400', bg: 'bg-green-500/20' },
  intermediate: { label: 'Intermediate', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  advanced: { label: 'Advanced', color: 'text-red-400', bg: 'bg-red-500/20' }
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const EQUIPMENT_OPTIONS = [
  'Dumbbells', 'Barbell', 'Cable Machine', 'Pull-up Bar', 'Kettlebells',
  'Resistance Bands', 'Bench', 'Squat Rack', 'Leg Press', 'Bodyweight Only'
];

const FOCUS_AREAS = [
  'Upper Body', 'Lower Body', 'Core', 'Full Body', 'Back', 'Chest',
  'Arms', 'Shoulders', 'Glutes', 'Cardio', 'HIIT', 'Mobility'
];

export default function ProgramsLibraryPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [myPrograms, setMyPrograms] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [generatedProgram, setGeneratedProgram] = useState(null);
  const [editingProgram, setEditingProgram] = useState(null);
  const [viewingProgram, setViewingProgram] = useState(null);
  const [subscribeModal, setSubscribeModal] = useState(null);
  const [activeTab, setActiveTab] = useState('library');
  
  // AI Generation form
  const [aiFormData, setAiFormData] = useState({
    category: 'muscle_building',
    difficulty: 'intermediate',
    duration_weeks: 4,
    workouts_per_week: 4,
    available_equipment: ['Dumbbells', 'Barbell', 'Cable Machine'],
    focus_areas: [],
    client_goals: ''
  });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'general_fitness',
    difficulty: 'beginner',
    duration_weeks: 4,
    workouts_per_week: 3,
    is_public: false,
    tags: [],
    equipment_needed: [],
    goals: [],
    weekly_schedule: []
  });
  
  // Subscribe form
  const [subscribeData, setSubscribeData] = useState({
    preferred_days: [],
    start_date: ''
  });
  
  const [tagInput, setTagInput] = useState('');
  const [equipmentInput, setEquipmentInput] = useState('');
  const [goalInput, setGoalInput] = useState('');

  const isTrainer = user?.role === 'trainer' || user?.role === 'admin';

  const fetchPrograms = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (categoryFilter) params.append('category', categoryFilter);
      if (difficultyFilter) params.append('difficulty', difficultyFilter);
      
      const [allRes, myRes] = await Promise.all([
        axios.get(`${API}/programs?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        isTrainer ? axios.get(`${API}/programs/my-programs`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve({ data: { programs: [] } })
      ]);
      
      setPrograms(allRes.data.programs || []);
      setMyPrograms(myRes.data.programs || []);
    } catch (error) {
      console.error('Error fetching programs:', error);
      toast.error('Failed to load programs');
    } finally {
      setLoading(false);
    }
  }, [token, searchTerm, categoryFilter, difficultyFilter, isTrainer]);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  const initializeSchedule = (weeks, workoutsPerWeek) => {
    const schedule = [];
    for (let week = 1; week <= weeks; week++) {
      const weekWorkouts = [];
      for (let day = 1; day <= workoutsPerWeek; day++) {
        weekWorkouts.push({
          day,
          workout: {
            name: `Week ${week} - Workout ${day}`,
            type: 'strength',
            duration_minutes: 45,
            exercises: []
          }
        });
      }
      schedule.push({ week, workouts: weekWorkouts });
    }
    return schedule;
  };

  const handleCreateProgram = () => {
    const schedule = initializeSchedule(4, 3);
    setFormData({
      name: '',
      description: '',
      category: 'general_fitness',
      difficulty: 'beginner',
      duration_weeks: 4,
      workouts_per_week: 3,
      is_public: false,
      tags: [],
      equipment_needed: [],
      goals: [],
      weekly_schedule: schedule
    });
    setEditingProgram(null);
    setShowCreateModal(true);
  };

  const handleAIGenerate = async () => {
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API}/programs/generate`, aiFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedProgram(res.data);
      toast.success('Program generated! Review and save below.');
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate program');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSaveGeneratedProgram = async () => {
    if (!generatedProgram) return;
    
    try {
      await axios.post(`${API}/programs`, generatedProgram, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('AI-generated program saved!');
      setShowAIModal(false);
      setGeneratedProgram(null);
      fetchPrograms();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save program');
    }
  };

  const toggleAIEquipment = (item) => {
    setAiFormData(prev => ({
      ...prev,
      available_equipment: prev.available_equipment.includes(item)
        ? prev.available_equipment.filter(e => e !== item)
        : [...prev.available_equipment, item]
    }));
  };

  const toggleAIFocus = (item) => {
    setAiFormData(prev => ({
      ...prev,
      focus_areas: prev.focus_areas.includes(item)
        ? prev.focus_areas.filter(f => f !== item)
        : [...prev.focus_areas, item]
    }));
  };

  const handleEditProgram = (program) => {
    setFormData({
      name: program.name,
      description: program.description,
      category: program.category,
      difficulty: program.difficulty,
      duration_weeks: program.duration_weeks,
      workouts_per_week: program.workouts_per_week,
      is_public: program.is_public,
      tags: program.tags || [],
      equipment_needed: program.equipment_needed || [],
      goals: program.goals || [],
      weekly_schedule: program.weekly_schedule || initializeSchedule(program.duration_weeks, program.workouts_per_week)
    });
    setEditingProgram(program);
    setShowCreateModal(true);
  };

  const handleSubmitProgram = async () => {
    if (!formData.name.trim()) {
      toast.error('Program name is required');
      return;
    }
    
    try {
      if (editingProgram) {
        await axios.put(`${API}/programs/${editingProgram.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Program updated successfully');
      } else {
        await axios.post(`${API}/programs`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Program created successfully');
      }
      setShowCreateModal(false);
      fetchPrograms();
    } catch (error) {
      console.error('Error saving program:', error);
      toast.error(error.response?.data?.detail || 'Failed to save program');
    }
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('Are you sure you want to delete this program?')) return;
    
    try {
      await axios.delete(`${API}/programs/${programId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Program deleted');
      fetchPrograms();
    } catch (error) {
      console.error('Error deleting program:', error);
      toast.error('Failed to delete program');
    }
  };

  const handleCloneProgram = async (programId) => {
    try {
      const res = await axios.post(`${API}/programs/${programId}/clone`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Program cloned successfully');
      fetchPrograms();
    } catch (error) {
      console.error('Error cloning program:', error);
      toast.error('Failed to clone program');
    }
  };

  const handleSubscribe = async () => {
    if (subscribeData.preferred_days.length < subscribeModal.workouts_per_week) {
      toast.error(`Please select at least ${subscribeModal.workouts_per_week} days`);
      return;
    }
    
    try {
      const payload = {
        program_id: subscribeModal.id,
        preferred_days: subscribeData.preferred_days,
        start_date: subscribeData.start_date || undefined
      };
      
      const res = await axios.post(`${API}/programs/${subscribeModal.id}/subscribe`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(res.data.message);
      setSubscribeModal(null);
      setSubscribeData({ preferred_days: [], start_date: '' });
      fetchPrograms();
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    }
  };

  const togglePreferredDay = (dayIndex) => {
    setSubscribeData(prev => {
      const days = prev.preferred_days.includes(dayIndex)
        ? prev.preferred_days.filter(d => d !== dayIndex)
        : [...prev.preferred_days, dayIndex];
      return { ...prev, preferred_days: days };
    });
  };

  const addTag = (type) => {
    const inputMap = { tags: tagInput, equipment_needed: equipmentInput, goals: goalInput };
    const setterMap = { tags: setTagInput, equipment_needed: setEquipmentInput, goals: setGoalInput };
    const value = inputMap[type].trim();
    
    if (value && !formData[type].includes(value)) {
      setFormData(prev => ({ ...prev, [type]: [...prev[type], value] }));
      setterMap[type]('');
    }
  };

  const removeTag = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const updateWorkout = (weekIndex, workoutIndex, field, value) => {
    setFormData(prev => {
      const newSchedule = [...prev.weekly_schedule];
      newSchedule[weekIndex].workouts[workoutIndex].workout[field] = value;
      return { ...prev, weekly_schedule: newSchedule };
    });
  };

  const ProgramCard = ({ program, isOwned = false }) => {
    const category = CATEGORY_CONFIG[program.category] || CATEGORY_CONFIG.general_fitness;
    const difficulty = DIFFICULTY_CONFIG[program.difficulty] || DIFFICULTY_CONFIG.beginner;
    const CategoryIcon = category.icon;
    
    return (
      <div 
        data-testid={`program-card-${program.id}`}
        className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-all"
      >
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2 rounded-lg ${category.bg}`}>
            <CategoryIcon className={`w-5 h-5 ${category.color}`} />
          </div>
          <div className="flex items-center gap-2">
            {program.is_public ? (
              <Globe className="w-4 h-4 text-green-400" />
            ) : (
              <Lock className="w-4 h-4 text-gray-500" />
            )}
            {program.stats?.avg_rating > 0 && (
              <div className="flex items-center gap-1 text-yellow-400">
                <Star className="w-4 h-4 fill-current" />
                <span className="text-sm">{program.stats.avg_rating.toFixed(1)}</span>
              </div>
            )}
          </div>
        </div>
        
        <h3 className="text-white font-semibold mb-1 line-clamp-1">{program.name}</h3>
        <p className="text-gray-400 text-sm mb-3 line-clamp-2">{program.description}</p>
        
        <div className="flex items-center gap-3 mb-3 text-sm">
          <span className={`px-2 py-0.5 rounded ${difficulty.bg} ${difficulty.color}`}>
            {difficulty.label}
          </span>
          <span className="text-gray-400">{program.duration_weeks} weeks</span>
          <span className="text-gray-400">{program.workouts_per_week}x/week</span>
        </div>
        
        {program.trainer_name && (
          <p className="text-xs text-gray-500 mb-3">By {program.trainer_name}</p>
        )}
        
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
          <Users className="w-3 h-3" />
          <span>{program.stats?.subscribers || 0} subscribers</span>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewingProgram(program)}
            className="flex-1"
            data-testid={`view-program-${program.id}`}
          >
            <Eye className="w-4 h-4 mr-1" />
            View
          </Button>
          
          {isOwned ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditProgram(program)}
                data-testid={`edit-program-${program.id}`}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDeleteProgram(program.id)}
                className="text-red-400 hover:text-red-300"
                data-testid={`delete-program-${program.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              {isTrainer && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCloneProgram(program.id)}
                  data-testid={`clone-program-${program.id}`}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              )}
              {!program.is_subscribed && (
                <Button
                  size="sm"
                  onClick={() => {
                    setSubscribeModal(program);
                    setSubscribeData({ preferred_days: [], start_date: '' });
                  }}
                  className="bg-primary"
                  data-testid={`subscribe-program-${program.id}`}
                >
                  <Play className="w-4 h-4 mr-1" />
                  Start
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="programs-library-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Workout Programs</h1>
          <p className="text-gray-400">Structured multi-week training templates</p>
        </div>
        
        {isTrainer && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowAIModal(true);
                setGeneratedProgram(null);
              }}
              data-testid="ai-generate-btn"
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              AI Generate
            </Button>
            <Button onClick={handleCreateProgram} data-testid="create-program-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Program
            </Button>
          </div>
        )}
      </div>

      {/* Tabs for trainers */}
      {isTrainer && (
        <div className="flex gap-2 border-b border-border pb-2">
          <button
            onClick={() => setActiveTab('library')}
            className={`px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === 'library' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-library"
          >
            Library ({programs.length})
          </button>
          <button
            onClick={() => setActiveTab('my-programs')}
            className={`px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === 'my-programs' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
            }`}
            data-testid="tab-my-programs"
          >
            My Programs ({myPrograms.length})
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search programs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary"
            data-testid="search-programs"
          />
        </div>
        
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 bg-card border border-border rounded-lg text-white focus:outline-none focus:border-primary"
          data-testid="filter-category"
        >
          <option value="">All Categories</option>
          {Object.entries(CATEGORY_CONFIG).map(([key, { name }]) => (
            <option key={key} value={key}>{name}</option>
          ))}
        </select>
        
        <select
          value={difficultyFilter}
          onChange={(e) => setDifficultyFilter(e.target.value)}
          className="px-4 py-2 bg-card border border-border rounded-lg text-white focus:outline-none focus:border-primary"
          data-testid="filter-difficulty"
        >
          <option value="">All Levels</option>
          {Object.entries(DIFFICULTY_CONFIG).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Programs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(activeTab === 'my-programs' ? myPrograms : programs).map(program => (
          <ProgramCard 
            key={program.id} 
            program={program} 
            isOwned={activeTab === 'my-programs' || program.trainer_id === user?.id}
          />
        ))}
        
        {(activeTab === 'my-programs' ? myPrograms : programs).length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-400">
            <Dumbbell className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{activeTab === 'my-programs' ? 'No programs created yet' : 'No programs found'}</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingProgram ? 'Edit Program' : 'Create Program'}
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Basic Info */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Program Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                  placeholder="e.g., 12-Week Muscle Builder"
                  data-testid="program-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white resize-none"
                  placeholder="Describe your program..."
                  data-testid="program-description-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                    data-testid="program-category-select"
                  >
                    {Object.entries(CATEGORY_CONFIG).map(([key, { name }]) => (
                      <option key={key} value={key}>{name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Difficulty</label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                    data-testid="program-difficulty-select"
                  >
                    {Object.entries(DIFFICULTY_CONFIG).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Duration (weeks)</label>
                  <input
                    type="number"
                    min="1"
                    max="52"
                    value={formData.duration_weeks}
                    onChange={(e) => {
                      const weeks = parseInt(e.target.value) || 1;
                      setFormData(prev => ({
                        ...prev,
                        duration_weeks: weeks,
                        weekly_schedule: initializeSchedule(weeks, prev.workouts_per_week)
                      }));
                    }}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                    data-testid="program-duration-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Workouts per Week</label>
                  <input
                    type="number"
                    min="1"
                    max="7"
                    value={formData.workouts_per_week}
                    onChange={(e) => {
                      const perWeek = parseInt(e.target.value) || 1;
                      setFormData(prev => ({
                        ...prev,
                        workouts_per_week: perWeek,
                        weekly_schedule: initializeSchedule(prev.duration_weeks, perWeek)
                      }));
                    }}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                    data-testid="program-workouts-per-week-input"
                  />
                </div>
              </div>
              
              {/* Tags */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Tags</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {formData.tags.map((tag, i) => (
                    <span key={i} className="px-2 py-1 bg-primary/20 text-primary rounded-full text-sm flex items-center gap-1">
                      {tag}
                      <button onClick={() => removeTag('tags', i)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag('tags'))}
                    className="flex-1 px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                    placeholder="Add tag..."
                  />
                  <Button variant="outline" onClick={() => addTag('tags')}>Add</Button>
                </div>
              </div>
              
              {/* Equipment */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Equipment Needed</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {formData.equipment_needed.map((eq, i) => (
                    <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm flex items-center gap-1">
                      {eq}
                      <button onClick={() => removeTag('equipment_needed', i)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={equipmentInput}
                    onChange={(e) => setEquipmentInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag('equipment_needed'))}
                    className="flex-1 px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                    placeholder="e.g., Dumbbells, Barbell..."
                  />
                  <Button variant="outline" onClick={() => addTag('equipment_needed')}>Add</Button>
                </div>
              </div>
              
              {/* Goals */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Goals</label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {formData.goals.map((goal, i) => (
                    <span key={i} className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-sm flex items-center gap-1">
                      {goal}
                      <button onClick={() => removeTag('goals', i)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={goalInput}
                    onChange={(e) => setGoalInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag('goals'))}
                    className="flex-1 px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                    placeholder="e.g., Build muscle, Increase strength..."
                  />
                  <Button variant="outline" onClick={() => addTag('goals')}>Add</Button>
                </div>
              </div>
              
              {/* Weekly Schedule */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Weekly Schedule</label>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {formData.weekly_schedule.map((week, weekIndex) => (
                    <div key={weekIndex} className="bg-secondary/50 rounded-lg p-3">
                      <h4 className="text-white font-medium mb-2">Week {week.week}</h4>
                      <div className="space-y-2">
                        {week.workouts.map((workout, workoutIndex) => (
                          <div key={workoutIndex} className="flex gap-2">
                            <input
                              type="text"
                              value={workout.workout.name}
                              onChange={(e) => updateWorkout(weekIndex, workoutIndex, 'name', e.target.value)}
                              className="flex-1 px-3 py-1.5 bg-card border border-border rounded text-white text-sm"
                              placeholder="Workout name"
                            />
                            <input
                              type="number"
                              value={workout.workout.duration_minutes}
                              onChange={(e) => updateWorkout(weekIndex, workoutIndex, 'duration_minutes', parseInt(e.target.value) || 30)}
                              className="w-20 px-3 py-1.5 bg-card border border-border rounded text-white text-sm"
                              placeholder="mins"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Public Toggle */}
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                <div>
                  <div className="text-white font-medium">Make Public</div>
                  <div className="text-sm text-gray-400">Allow other trainers and clients to see this program</div>
                </div>
                <button
                  onClick={() => setFormData(prev => ({ ...prev, is_public: !prev.is_public }))}
                  className={`w-12 h-6 rounded-full transition-colors ${formData.is_public ? 'bg-primary' : 'bg-gray-600'}`}
                  data-testid="program-public-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${formData.is_public ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-card border-t border-border p-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSubmitProgram} className="flex-1" data-testid="save-program-btn">
                {editingProgram ? 'Update Program' : 'Create Program'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* View Program Modal */}
      {viewingProgram && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">{viewingProgram.name}</h2>
              <button onClick={() => setViewingProgram(null)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <p className="text-gray-300">{viewingProgram.description}</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-secondary/50 rounded-lg p-3">
                  <div className="text-gray-400 text-sm">Duration</div>
                  <div className="text-white font-medium">{viewingProgram.duration_weeks} weeks</div>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3">
                  <div className="text-gray-400 text-sm">Frequency</div>
                  <div className="text-white font-medium">{viewingProgram.workouts_per_week}x per week</div>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3">
                  <div className="text-gray-400 text-sm">Total Workouts</div>
                  <div className="text-white font-medium">{viewingProgram.total_workouts}</div>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3">
                  <div className="text-gray-400 text-sm">Difficulty</div>
                  <div className={`font-medium ${DIFFICULTY_CONFIG[viewingProgram.difficulty]?.color}`}>
                    {DIFFICULTY_CONFIG[viewingProgram.difficulty]?.label}
                  </div>
                </div>
              </div>
              
              {viewingProgram.equipment_needed?.length > 0 && (
                <div>
                  <div className="text-gray-400 text-sm mb-2">Equipment Needed</div>
                  <div className="flex gap-2 flex-wrap">
                    {viewingProgram.equipment_needed.map((eq, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">{eq}</span>
                    ))}
                  </div>
                </div>
              )}
              
              {viewingProgram.goals?.length > 0 && (
                <div>
                  <div className="text-gray-400 text-sm mb-2">Goals</div>
                  <div className="flex gap-2 flex-wrap">
                    {viewingProgram.goals.map((goal, i) => (
                      <span key={i} className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">{goal}</span>
                    ))}
                  </div>
                </div>
              )}
              
              <div>
                <div className="text-gray-400 text-sm mb-2">Weekly Schedule</div>
                <div className="space-y-2">
                  {viewingProgram.weekly_schedule?.slice(0, 4).map((week, i) => (
                    <div key={i} className="bg-secondary/50 rounded-lg p-3">
                      <div className="text-white font-medium mb-1">Week {week.week}</div>
                      <div className="text-gray-400 text-sm">
                        {week.workouts.map(w => w.workout.name).join(' • ')}
                      </div>
                    </div>
                  ))}
                  {viewingProgram.weekly_schedule?.length > 4 && (
                    <div className="text-gray-500 text-sm text-center">
                      + {viewingProgram.weekly_schedule.length - 4} more weeks
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-card border-t border-border p-4 flex gap-3">
              <Button variant="outline" onClick={() => setViewingProgram(null)} className="flex-1">
                Close
              </Button>
              {!viewingProgram.is_subscribed && viewingProgram.trainer_id !== user?.id && (
                <Button 
                  onClick={() => {
                    setSubscribeModal(viewingProgram);
                    setViewingProgram(null);
                    setSubscribeData({ preferred_days: [], start_date: '' });
                  }} 
                  className="flex-1"
                  data-testid="start-program-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Program
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Subscribe Modal */}
      {subscribeModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md">
            <div className="border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Start Program</h2>
              <button onClick={() => setSubscribeModal(null)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="bg-secondary/50 rounded-lg p-3">
                <div className="text-white font-medium">{subscribeModal.name}</div>
                <div className="text-gray-400 text-sm">
                  {subscribeModal.duration_weeks} weeks • {subscribeModal.workouts_per_week}x per week
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Select your preferred workout days (at least {subscribeModal.workouts_per_week})
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {DAYS.map((day, index) => (
                    <button
                      key={index}
                      onClick={() => togglePreferredDay(index)}
                      className={`p-2 rounded-lg text-sm transition-colors ${
                        subscribeData.preferred_days.includes(index)
                          ? 'bg-primary text-white'
                          : 'bg-secondary text-gray-400 hover:text-white'
                      }`}
                      data-testid={`select-day-${index}`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Start Date (optional)</label>
                <input
                  type="date"
                  value={subscribeData.start_date}
                  onChange={(e) => setSubscribeData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                  data-testid="start-date-input"
                />
              </div>
            </div>
            
            <div className="border-t border-border p-4 flex gap-3">
              <Button variant="outline" onClick={() => setSubscribeModal(null)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSubscribe} className="flex-1" data-testid="confirm-subscribe-btn">
                <Check className="w-4 h-4 mr-2" />
                Start Program
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AI Generation Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Wand2 className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">AI Program Generator</h2>
                  <p className="text-sm text-gray-400">Generate a complete workout program with AI</p>
                </div>
              </div>
              <button onClick={() => setShowAIModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 space-y-6">
              {!generatedProgram ? (
                <>
                  {/* AI Form */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Goal Category</label>
                      <select
                        value={aiFormData.category}
                        onChange={(e) => setAiFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                        data-testid="ai-category-select"
                      >
                        {Object.entries(CATEGORY_CONFIG).map(([key, { name }]) => (
                          <option key={key} value={key}>{name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Difficulty Level</label>
                      <select
                        value={aiFormData.difficulty}
                        onChange={(e) => setAiFormData(prev => ({ ...prev, difficulty: e.target.value }))}
                        className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                        data-testid="ai-difficulty-select"
                      >
                        {Object.entries(DIFFICULTY_CONFIG).map(([key, { label }]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Program Duration</label>
                      <select
                        value={aiFormData.duration_weeks}
                        onChange={(e) => setAiFormData(prev => ({ ...prev, duration_weeks: parseInt(e.target.value) }))}
                        className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                        data-testid="ai-duration-select"
                      >
                        <option value={4}>4 Weeks</option>
                        <option value={6}>6 Weeks</option>
                        <option value={8}>8 Weeks</option>
                        <option value={12}>12 Weeks</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Workouts per Week</label>
                      <select
                        value={aiFormData.workouts_per_week}
                        onChange={(e) => setAiFormData(prev => ({ ...prev, workouts_per_week: parseInt(e.target.value) }))}
                        className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                        data-testid="ai-frequency-select"
                      >
                        <option value={2}>2x per week</option>
                        <option value={3}>3x per week</option>
                        <option value={4}>4x per week</option>
                        <option value={5}>5x per week</option>
                        <option value={6}>6x per week</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Available Equipment</label>
                    <div className="flex flex-wrap gap-2">
                      {EQUIPMENT_OPTIONS.map((item) => (
                        <button
                          key={item}
                          onClick={() => toggleAIEquipment(item)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            aiFormData.available_equipment.includes(item)
                              ? 'bg-blue-500/30 text-blue-400 border border-blue-500/50'
                              : 'bg-secondary text-gray-400 hover:text-white'
                          }`}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Focus Areas (optional)</label>
                    <div className="flex flex-wrap gap-2">
                      {FOCUS_AREAS.map((item) => (
                        <button
                          key={item}
                          onClick={() => toggleAIFocus(item)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            aiFormData.focus_areas.includes(item)
                              ? 'bg-green-500/30 text-green-400 border border-green-500/50'
                              : 'bg-secondary text-gray-400 hover:text-white'
                          }`}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Client Goals (optional)</label>
                    <textarea
                      value={aiFormData.client_goals}
                      onChange={(e) => setAiFormData(prev => ({ ...prev, client_goals: e.target.value }))}
                      rows={2}
                      className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white resize-none"
                      placeholder="e.g., Train for a marathon, Build upper body strength, Lose 20 lbs..."
                      data-testid="ai-goals-input"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Generated Program Preview */}
                  <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                      <span className="text-purple-400 font-medium">AI Generated Program</span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{generatedProgram.name}</h3>
                    <p className="text-gray-300">{generatedProgram.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-secondary/50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-white">{generatedProgram.duration_weeks}</div>
                      <div className="text-xs text-gray-400">Weeks</div>
                    </div>
                    <div className="bg-secondary/50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-white">{generatedProgram.workouts_per_week}</div>
                      <div className="text-xs text-gray-400">Per Week</div>
                    </div>
                    <div className="bg-secondary/50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-white">{generatedProgram.duration_weeks * generatedProgram.workouts_per_week}</div>
                      <div className="text-xs text-gray-400">Total Workouts</div>
                    </div>
                    <div className="bg-secondary/50 rounded-lg p-3 text-center">
                      <div className={`text-lg font-bold ${DIFFICULTY_CONFIG[generatedProgram.difficulty]?.color}`}>
                        {DIFFICULTY_CONFIG[generatedProgram.difficulty]?.label}
                      </div>
                      <div className="text-xs text-gray-400">Difficulty</div>
                    </div>
                  </div>
                  
                  {generatedProgram.equipment_needed?.length > 0 && (
                    <div>
                      <div className="text-sm text-gray-400 mb-2">Equipment Needed</div>
                      <div className="flex flex-wrap gap-2">
                        {generatedProgram.equipment_needed.map((eq, i) => (
                          <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">{eq}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <div className="text-sm text-gray-400 mb-2">Weekly Schedule Preview</div>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {generatedProgram.weekly_schedule?.slice(0, 4).map((week, i) => (
                        <div key={i} className="bg-secondary/50 rounded-lg p-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-white font-medium">Week {week.week}</span>
                            {week.focus && <span className="text-xs text-gray-400">{week.focus}</span>}
                          </div>
                          <div className="space-y-1">
                            {week.workouts.map((w, j) => (
                              <div key={j} className="flex justify-between text-sm">
                                <span className="text-gray-300">{w.workout.name}</span>
                                <span className="text-gray-500">{w.workout.exercises?.length || 0} exercises</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {generatedProgram.weekly_schedule?.length > 4 && (
                        <div className="text-center text-gray-500 text-sm py-2">
                          + {generatedProgram.weekly_schedule.length - 4} more weeks...
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div className="sticky bottom-0 bg-card border-t border-border p-4 flex gap-3">
              {!generatedProgram ? (
                <>
                  <Button variant="outline" onClick={() => setShowAIModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAIGenerate} 
                    disabled={aiGenerating}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                    data-testid="generate-program-btn"
                  >
                    {aiGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4 mr-2" />
                        Generate Program
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => setGeneratedProgram(null)} 
                    className="flex-1"
                  >
                    Regenerate
                  </Button>
                  <Button 
                    onClick={handleSaveGeneratedProgram}
                    className="flex-1"
                    data-testid="save-generated-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Save Program
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
